import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Loader2, RefreshCw, Trophy } from 'lucide-react'
import { algorithmicScannerService } from '@/services/algorithmicScanner'

function formatPct(v) {
  if (v === undefined || v === null || Number.isNaN(v)) return '—'
  const num = Number(v)
  const sign = num >= 0 ? '+' : ''
  return `${sign}${(num * 100).toFixed(2)}%`
}

function formatNum(v, digits = 2) {
  if (v === undefined || v === null || Number.isNaN(v)) return '—'
  return Number(v).toFixed(digits)
}

function scoreBadge(score) {
  if (score >= 80) return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
  if (score >= 60) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
  return 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
}

export default function MonteCarloRankingsPage() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [scannerFilter, setScannerFilter] = useState('all')
  const [assetFilter, setAssetFilter] = useState('')

  const fetchRankings = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await algorithmicScannerService.getMonteCarloRankings()
      setItems(data.items || [])
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar el ranking.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRankings()
  }, [])

  const scanners = useMemo(() => {
    const map = new Map()
    items.forEach((it) => map.set(it.scanner_id, it.scanner_name))
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [items])

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (scannerFilter !== 'all' && it.scanner_id !== scannerFilter) return false
      if (assetFilter && !it.asset.toLowerCase().includes(assetFilter.toLowerCase())) return false
      return true
    })
  }, [items, scannerFilter, assetFilter])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/scanner-algorithmic" className="p-2 rounded-lg bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors">
            <ArrowLeft size={22} />
          </Link>
          <div className="p-2 rounded-lg bg-purple-600/10 text-purple-600 dark:text-purple-400">
            <Trophy size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Ranking Monte Carlo</h1>
            <p className="text-xs text-slate-500 dark:text-gray-400">
              Combinaciones activo/timeframe ordenadas por score entre todos tus scanners
            </p>
          </div>
        </div>
        <button
          onClick={fetchRankings}
          disabled={loading}
          className="btn-secondary flex items-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          Refrescar
        </button>
      </div>

      <div className="card space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={scannerFilter}
            onChange={(e) => setScannerFilter(e.target.value)}
            className="input w-full sm:w-64"
          >
            <option value="all">Todos los scanners</option>
            {scanners.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <input
            type="text"
            value={assetFilter}
            onChange={(e) => setAssetFilter(e.target.value)}
            placeholder="Filtrar por activo..."
            className="input w-full sm:w-64"
          />
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 text-red-700 dark:text-red-300 text-sm border border-red-500/30">
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={32} className="animate-spin text-slate-400" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-gray-400">
            <p className="text-sm">No hay rankings disponibles.</p>
            <p className="text-xs mt-1">Ejecuta Monte Carlo desde un scanner para generar datos.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-400">
                <tr>
                  <th className="text-left p-3 rounded-l-lg w-16">#</th>
                  <th className="text-left p-3">Scanner</th>
                  <th className="text-left p-3">Activo</th>
                  <th className="text-left p-3">Timeframe</th>
                  <th className="text-right p-3">Score</th>
                  <th className="text-right p-3">Net profit</th>
                  <th className="text-right p-3">Profit factor</th>
                  <th className="text-right p-3">Sharpe</th>
                  <th className="text-right p-3">Max DD</th>
                  <th className="text-right p-3">Trades</th>
                  <th className="text-right p-3 rounded-r-lg">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it) => (
                  <tr key={`${it.scanner_id}-${it.asset}-${it.timeframe}`} className="border-b border-slate-200 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/50">
                    <td className="p-3 font-bold text-slate-700 dark:text-gray-200">{it.rank}</td>
                    <td className="p-3 text-slate-700 dark:text-gray-200">{it.scanner_name}</td>
                    <td className="p-3 font-medium text-slate-900 dark:text-white">{it.asset}</td>
                    <td className="p-3 text-slate-600 dark:text-gray-300">{it.timeframe}</td>
                    <td className="text-right p-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${scoreBadge(it.score)}`}>
                        {formatNum(it.score, 1)}
                      </span>
                    </td>
                    <td className={`text-right p-3 font-medium ${it.net_profit_pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {formatPct(it.net_profit_pct)}
                    </td>
                    <td className="text-right p-3">{formatNum(it.profit_factor, 2)}</td>
                    <td className="text-right p-3">{formatNum(it.sharpe, 2)}</td>
                    <td className="text-right p-3 text-rose-600 dark:text-rose-400">{formatPct(it.max_drawdown_pct)}</td>
                    <td className="text-right p-3">{it.total_trades}</td>
                    <td className="text-right p-3 text-xs text-slate-500 dark:text-gray-400">
                      {it.created_at ? new Date(it.created_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-[10px] text-slate-500 dark:text-gray-500">
        Disclaimer: este ranking se genera a partir de métricas históricas simuladas; no es asesoramiento financiero.
      </p>
    </div>
  )
}
